<template>
  <!-- Overlay backdrop -->
  <teleport to="body">
    <div
       v-if="visible && !isLoading && activeQuestions.length"
      class="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-4"
    >
      <!-- Popup Panel -->
      <div
        v-if="visible"
        class="bg-white w-full sm:max-w-lg sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden"
      >
        <!-- Header -->
        <div
          class="relative bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 flex-shrink-0"
        >
          <!-- Progress bar -->
          <!-- Continuous Progress Bar Container -->
          <div
            class="w-full bg-white/25 h-1 rounded-full overflow-hidden mb-3.5"
          >
            <div
              class="bg-white h-full transition-all duration-500 ease-out"
              :style="{
                width: activeQuestions.length
                  ? `${((currentIndex + 1) / activeQuestions.length) * 100}%`
                  : '0%',
              }"
            ></div>
          </div>
          <div class="flex items-start justify-between gap-4">
            <div>
              <p
                class="text-blue-100 text-xs font-semibold tracking-wide uppercase mb-0.5"
              >
                Question {{ currentIndex + 1 }} of {{ activeQuestions.length }}
              </p>
              <h2 class="text-white font-bold text-base leading-snug">
                {{ surveyData ? surveyData.survey : "Survey" }}
              </h2>
            </div>
            <button
              @click="closePopup"
              class="text-white/70 hover:text-white transition-colors flex-shrink-0 mt-0.5"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <!-- Percentage -->
          <p class="text-blue-200 text-xs mt-2">
            {{ Math.round((currentIndex / activeQuestions.length) * 100) }}%
            completed
          </p>
        </div>

        <!-- Thank-you Screen -->
        <div
          v-if="submitted"
          class="flex-1 flex flex-col items-center justify-center gap-4 px-8 py-12 text-center"
        >
          <div
            class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-bounce-in"
          >
            <svg
              class="w-10 h-10 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h3 class="text-xl font-extrabold text-slate-800">Thank You!</h3>
            <p class="text-gray-500 text-sm mt-1.5 max-w-xs">
              Your response has been recorded. We appreciate your valuable
              feedback.
            </p>
          </div>
          <button
            @click="closePopup"
            class="bg-blues hover:bg-hover text-white font-semibold px-8 py-2.5 rounded-xl transition-all shadow-sm"
          >
            Close
          </button>
        </div>

        <!-- Question Body -->
        <div v-else class="flex-1 overflow-y-auto px-6 py-5">
          <div v-if="currentQ">
            <div class="flex items-start gap-2 mb-5">
              <span
                class="flex-shrink-0 w-7 h-7 rounded-lg bg-blues text-white text-xs font-black flex items-center justify-center mt-0.5"
              >
                {{ currentIndex + 1 }}
              </span>
              <div>
                <h3 class="text-slate-800 font-bold text-base leading-snug">
                  {{ currentQ.question_text }}
                </h3>
                <span
                  v-if="currentQ.required"
                  class="text-xs text-red-500 font-medium"
                  >* Required</span
                >
              </div>
            </div>

            <!-- Validation Error Message -->
            <div v-if="validationError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
              <p class="text-sm text-red-600 flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Please answer this required question before proceeding.
              </p>
            </div>

            <!-- ===== Render by question_type ===== -->

            <!-- Radio Button -->
            <div
              v-if="currentQ.question_type?.title === 'radio'"
              class="space-y-2.5"
            >
              <label
                v-for="opt in currentQ.config?.options || []"
                :key="opt"
                :class="[
                  'flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all',
                  answers[currentIndex] === opt
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-100 hover:border-slate-300 bg-slate-50',
                ]"
              >
                <span
                  :class="[
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all',
                    answers[currentIndex] === opt
                      ? 'border-blue-500'
                      : 'border-slate-300',
                  ]"
                >
                  <span
                    v-if="answers[currentIndex] === opt"
                    class="w-2 h-2 rounded-full bg-blue-500"
                  ></span>
                </span>

                <input
                  type="radio"
                  :value="opt"
                  v-model="answers[currentIndex]"
                  class="sr-only"
                  @change="clearValidationError"
                />

                <span class="text-sm font-medium text-slate-700">
                  {{ opt }}
                </span>
              </label>
            </div>

            <!-- Multiple Choice (question_type: 21) -->
            <!-- Checkbox -->
            <div
              v-else-if="currentQ.question_type?.title === 'checkbox'"
              class="space-y-2.5"
            >
              <label
                v-for="opt in currentQ.config?.options || []"
                :key="opt"
                :class="[
                  'flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all',
                  (answers[currentIndex] || []).includes(opt)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-100 hover:border-slate-300 bg-slate-50',
                ]"
              >
                <span
                  :class="[
                    'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all',
                    (answers[currentIndex] || []).includes(opt)
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-slate-300',
                  ]"
                >
                  <svg
                    v-if="(answers[currentIndex] || []).includes(opt)"
                    class="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>

                <input
                  type="checkbox"
                  :value="opt"
                  v-model="answers[currentIndex]"
                  class="sr-only"
                  @change="clearValidationError"
                />

                <span class="text-sm font-medium text-slate-700">
                  {{ opt }}
                </span>
              </label>
            </div>

            <!-- Dropdown (question_type: 22) -->
            <!-- Dropdown -->
            <div v-else-if="currentQ.question_type?.title === 'dropdown'">
              <select
                v-model="answers[currentIndex]"
                class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @change="clearValidationError"
              >
                <option value="">Select an option...</option>

                <option
                  v-for="opt in currentQ.config?.options || []"
                  :key="opt"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>
            </div>

            <!-- Rating Scale 1-100 (question_type: 30) -->

            <div
              v-else-if="currentQ.question_type.title === 'rating'"
              class="space-y-3"
            >
              <input
                v-model.number="answers[currentIndex]"
                type="range"
                :min="currentQ.config.min || 0"
                :max="currentQ.config.max || 100"
                :step="1"
                class="w-full accent-blue-600"
                @change="clearValidationError"
              />
              <div
                class="flex justify-between text-xs text-gray-400 font-medium"
              >
                <span>{{ currentQ.config.min || 0 }}</span>
                <span class="text-blue-700 font-bold text-sm">
                  {{
                    answers[currentIndex] !== undefined
                      ? answers[currentIndex]
                      : currentQ.config.min || 0
                  }}
                </span>
                <span>{{ currentQ.config.max || 100 }}</span>
              </div>
            </div>

            <!-- Likert Scale -->
            <div
              v-else-if="currentQ.question_type?.title === 'likert'"
              class="space-y-2"
            >
              <label
                v-for="opt in currentQ.config?.options || []"
                :key="opt"
                :class="[
                  'flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all',
                  answers[currentIndex] === opt
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-100 hover:border-slate-300 bg-slate-50',
                ]"
              >
                <span
                  :class="[
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    answers[currentIndex] === opt
                      ? 'border-blue-500'
                      : 'border-slate-300',
                  ]"
                >
                  <span
                    v-if="answers[currentIndex] === opt"
                    class="w-2 h-2 rounded-full bg-blue-500"
                  ></span>
                </span>

                <input
                  type="radio"
                  :value="opt"
                  v-model="answers[currentIndex]"
                  class="sr-only"
                  @change="clearValidationError"
                />

                <span class="text-sm font-medium text-slate-700">
                  {{ opt }}
                </span>
              </label>
            </div>

            <!-- Star Rating -->
            <div
              v-else-if="currentQ.question_type?.title === 'star'"
              class="flex gap-2 flex-wrap"
            >
              <button
                v-for="s in currentQ.config?.max_stars || 5"
                :key="s"
                @click="selectStar(s)"
                class="transition-transform hover:scale-110 focus:outline-none"
              >
                <svg
                  :class="[
                    'w-9 h-9 transition-colors',
                    s <= (answers[currentIndex] || 0)
                      ? 'text-amber-400 fill-current'
                      : 'text-slate-200 fill-current hover:text-amber-300',
                  ]"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                  />
                </svg>
              </button>

              <p
                v-if="answers[currentIndex]"
                class="w-full text-xs text-amber-600 font-semibold mt-1"
              >
                {{
                  [
                    "",
                    "Poor",
                    "Fair",
                    "Good",
                    "Very Good",
                    "Excellent",
                    "Outstanding",
                    "Brilliant",
                    "Superb",
                    "Almost Perfect",
                    "Perfect",
                  ][answers[currentIndex]] || `${answers[currentIndex]} stars`
                }}
              </p>
            </div>

            <!-- Yes/No (question_type: 26) -->
            <!-- Yes / No -->
            <div
              v-else-if="currentQ.question_type?.title === 'yes_no'"
              class="grid grid-cols-2 gap-3"
            >
              <button
                @click="selectYesNo('Yes')"
                :class="[
                  'flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border-2 font-semibold text-sm transition-all',
                  answers[currentIndex] === 'Yes'
                    ? 'bg-green-600 text-white border-green-600 shadow-md shadow-green-100'
                    : 'border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:border-slate-300',
                ]"
              >
                <span class="text-base">✅</span>
                <span>Yes</span>
              </button>

              <button
                @click="selectYesNo('No')"
                :class="[
                  'flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border-2 font-semibold text-sm transition-all',
                  answers[currentIndex] === 'No'
                    ? 'bg-red-600 text-white border-red-600 shadow-md shadow-red-100'
                    : 'border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:border-slate-300',
                ]"
              >
                <span class="text-base">❌</span>
                <span>No</span>
              </button>
            </div>

            <!-- True/False (question_type: 27) -->
            <!-- True / False -->
            <div
              v-else-if="currentQ.question_type?.title === 'true_false'"
              class="grid grid-cols-2 gap-3"
            >
              <button
                @click="selectTrueFalse('True')"
                :class="[
                  'flex items-center justify-center py-2.5 px-4 rounded-xl border-2 font-semibold text-sm transition-all',
                  answers[currentIndex] === 'True'
                    ? 'bg-blues text-white border-blue-600 shadow-md shadow-blue-100'
                    : 'border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:border-slate-300',
                ]"
              >
                True
              </button>

              <button
                @click="selectTrueFalse('False')"
                :class="[
                  'flex items-center justify-center py-2.5 px-4 rounded-xl border-2 font-semibold text-sm transition-all',
                  answers[currentIndex] === 'False'
                    ? 'bg-orange-600 text-white border-orange-600 shadow-md shadow-orange-100'
                    : 'border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:border-slate-300',
                ]"
              >
                False
              </button>
            </div>

            <!-- Short Text (question_type: 28) -->
            <!-- Short Text -->
            <div v-else-if="currentQ.question_type?.title === 'short_text'">
              <input
                v-model="answers[currentIndex]"
                type="text"
                placeholder="Your answer..."
                class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400"
                @input="clearValidationError"
              />
            </div>

            <!-- Long Text (question_type: 29) -->
            <div v-else-if="currentQ.question_type.title === 'long_text'">
              <textarea
                v-model="answers[currentIndex]"
                rows="4"
                placeholder="Write your detailed response here..."
                class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400 resize-none"
                @input="clearValidationError"
              ></textarea>
            </div>

            <!-- Number -->
            <div v-else-if="currentQ.question_type?.title === 'number'">
              <div class="flex items-center gap-3">
                <input
                  v-model.number="answers[currentIndex]"
                  type="number"
                  :min="currentQ.config?.min ?? 0"
                  :max="currentQ.config?.max ?? 100"
                  placeholder="Enter a number"
                  class="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  @input="clearValidationError"
                />
              </div>
            </div>

            <!-- Phone Number (question_type: 31) -->
            <div v-else-if="currentQ.question_type.title === 'phone'">
              <input
                type="tel"
                v-model="answers[currentIndex]"
                placeholder="+91 XXXXX XXXXX"
                class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                @input="clearValidationError"
              />
            </div>

            <!-- URL Input (question_type: 32) -->
            <div v-else-if="currentQ.question_type.title === 'url'">
              <input
                type="url"
                v-model="answers[currentIndex]"
                placeholder="https://"
                class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                @input="clearValidationError"
              />
            </div>

            <!-- Date & Time (question_type: 33) -->
            <div
              v-else-if="currentQ.question_type.title === 'date_time'"
              class="grid grid-cols-2 gap-3"
            >
              <div>
                <label class="text-xs font-semibold text-gray-500 block mb-1.5">
                  Date
                </label>
                <input
                  type="date"
                  v-model="answers[currentIndex + '_date']"
                  class="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  @change="clearValidationError"
                />
              </div>

              <div>
                <label class="text-xs font-semibold text-gray-500 block mb-1.5">
                  Time
                </label>
                <input
                  type="time"
                  v-model="answers[currentIndex + '_time']"
                  class="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  @change="clearValidationError"
                />
              </div>
            </div>

            <!-- Matrix/Grid Rating (question_type: 34) -->
            <div
              v-else-if="currentQ.question_type.title === 'matrix'"
              class="overflow-x-auto"
            >
              <table class="min-w-full text-xs">
                <thead>
                  <tr>
                    <th class="w-32"></th>
                    <th
                      v-for="col in currentQ.config.columns"
                      :key="col"
                      class="px-2 py-1 font-semibold text-slate-600 text-center"
                    >
                      {{ col }}
                    </th>
                  </tr>
                </thead>

                <tbody class="divide-y divide-slate-100">
                  <tr
                    v-for="row in currentQ.config.rows"
                    :key="row"
                    class="hover:bg-slate-50"
                  >
                    <td class="py-2 pr-3 font-medium text-slate-700">
                      {{ row }}
                    </td>

                    <td
                      v-for="col in currentQ.config.columns"
                      :key="col"
                      class="px-2 py-2 text-center"
                    >
                      <button
                        @click="setMatrix(currentIndex, row, col)"
                        :class="[
                          'w-7 h-7 rounded-full border-2 mx-auto flex items-center justify-center transition-all',
                          getMatrix(currentIndex, row) === col
                            ? 'bg-blues border-blue-600'
                            : 'border-slate-300 hover:border-blue-400',
                        ]"
                      >
                        <span
                          v-if="getMatrix(currentIndex, row) === col"
                          class="w-2 h-2 bg-white rounded-full"
                        ></span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Slider (question_type: 35) -->
            <div
              v-else-if="currentQ.question_type.title === 'slider'"
              class="space-y-3"
            >
              <input
                v-model.number="answers[currentIndex]"
                type="range"
                :min="currentQ.config.min || 0"
                :max="currentQ.config.max || 100"
                :step="currentQ.config.step || 1"
                class="w-full accent-blue-600"
                @change="clearValidationError"
              />
              <div
                class="flex justify-between text-xs text-gray-400 font-medium"
              >
                <span>{{ currentQ.config.min || 0 }}</span>
                <span class="text-blue-700 font-bold text-sm">
                  {{
                    answers[currentIndex] !== undefined
                      ? answers[currentIndex]
                      : currentQ.config.min || 0
                  }}
                </span>
                <span>{{ currentQ.config.max || 100 }}</span>
              </div>
            </div>

            <!-- Image Choice - UPDATED -->
            <div
              v-else-if="currentQ.question_type.title === 'image_choice'"
              class="grid grid-cols-2 gap-4"
            >
              <button
                v-for="(opt, ii) in currentQ.config.options"
                :key="ii"
                @click="selectImageChoice(ii)"
                :class="[
                  'rounded-2xl border-2 overflow-hidden transition-all aspect-square flex flex-col items-center justify-center p-2',
                  answers[currentIndex] === ii
                    ? 'border-blue-500 ring-2 ring-blue-300 scale-[1.02] shadow-lg'
                    : 'border-slate-200 hover:border-blue-300 hover:shadow-md',
                ]"
              >
                <div class="w-full h-32 flex items-center justify-center bg-slate-50 rounded-lg overflow-hidden">
                  <img
                    v-if="opt.image"
                    :src="opt.image"
                    :alt="opt.label || 'Image ' + (ii + 1)"
                    class="w-full h-full object-contain"
                    @error="handleImageError($event, ii)"
                  />
                  <div v-else class="text-6xl">🖼️</div>
                </div>
                <span class="text-xs font-medium text-slate-600 mt-2">
                  {{ opt.label || "Option " + (ii + 1) }}
                </span>
              </button>
            </div>

            <!-- Consent Checkbox (question_type: 37) -->
            <div v-else-if="currentQ.question_type.title === 'consent'">
              <label
                :class="[
                  'flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all',
                  answers[currentIndex]
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-slate-50',
                ]"
              >
                <span
                  :class="[
                    'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all',
                    answers[currentIndex]
                      ? 'bg-blues border-blue-600'
                      : 'border-slate-400',
                  ]"
                >
                  <svg
                    v-if="answers[currentIndex]"
                    class="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>

                <input
                  type="checkbox"
                  v-model="answers[currentIndex]"
                  class="sr-only"
                  @change="clearValidationError"
                />

                <span class="text-sm text-slate-700 leading-snug">
                  {{
                    currentQ.config.consent_text ||
                    "I agree to the terms and conditions."
                  }}
                </span>
              </label>
            </div>

            <!-- Read-only Information (question_type: 38) -->
            <div
              v-else-if="currentQ.question_type.title === 'readonly'"
              class="bg-blue-50 border border-blue-100 rounded-2xl p-4"
            >
              <div class="flex items-start gap-3">
                <svg
                  class="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>

                <div class="text-sm text-blue-700 leading-relaxed" v-html="currentQ.config.content || 'Please read the instructions below before proceeding.'">
                </div>
              </div>
              <!-- Store a default answer for readonly questions -->
              <input type="hidden" :value="answers[currentIndex] = 'read'">
            </div>

            <!-- Fallback for unknown types -->
            <div v-else class="text-sm text-gray-500">
              <p>
                Question type not recognized (ID: {{ currentQ.question_type }})
              </p>
              <p class="text-xs text-gray-400 mt-1">
                Please contact support if you see this message.
              </p>
            </div>
          </div>
        </div>

        <!-- Footer Navigation -->
        <div
          v-if="!submitted"
          class="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex items-center justify-between gap-3 bg-white"
        >
          <button
            @click="prev"
            :disabled="currentIndex === 0"
            class="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:border-slate-300 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>

          <p class="text-xs text-gray-400 font-medium">
            {{ currentIndex + 1 }} / {{ activeQuestions.length }}
          </p>

          <button
            v-if="currentIndex < activeQuestions.length - 1"
            @click="next"
            :disabled="isNextDisabled"
            class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blues hover:bg-hover text-white font-semibold text-sm transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
          <button
            v-else
            @click="submit"
            :disabled="isSubmitDisabled"
            class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";

export default {
  name: "SurveyPopup",
  props: {
    modelValue: { type: Boolean, default: false },
    surveyId: { type: [String, Number], default: null },
  },
  emits: ["update:modelValue", "survey-submitted"],
  data() {
    return {
      visible: this.modelValue,
      currentIndex: 0,
      submitted: false,
      answers: {},
      matrixAnswers: {},
      surveyData: null,
      isLoading: false,
      validationError: false,
      disable_submit: false,
      messages: {
        text: '',
        type: 'null'
      }
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    activeQuestions() {
      if (!this.surveyData) return [];
      // Filter only published questions
      return this.surveyData.filter((q) => q.is_published !== false);
    },
    currentQ() {
      return this.activeQuestions[this.currentIndex];
    },
    isNextDisabled() {
      if (!this.currentQ) return true;
      // If question is required, check if it's answered
      if (this.currentQ.required) {
        return !this.isQuestionAnswered(this.currentIndex);
      }
      // Non-required questions can be skipped
      return false;
    },
    isSubmitDisabled() {
      // Check if all required questions are answered
      return this.activeQuestions.some((q, idx) => {
        if (q.required) {
          return !this.isQuestionAnswered(idx);
        }
        return false;
      });
    }
  },
  created() {
    // this.fetchSurveyData();
  },
  watch: {
    modelValue(val) {
      this.visible = val;
    },

    visible(val) {
      this.$emit("update:modelValue", val);
    },

    surveyId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.fetchSurveyData(id);
        } else {
          this.surveyData = [];
        }
      },
    },
  },
  methods: {
    async fetchSurveyData(surveyId) {
      this.isLoading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/survey-question/list/?Key=${this.userInfo.token_key}&survey__id=${surveyId}&is_published=true`,
        );
        if (response.data && response.data.data) {
          // Show only questions not yet submitted
          this.surveyData = response.data.data.filter(
            (question) => question.student_submit === false,
          );

          this.currentIndex = 0;
          this.answers = {};
          this.matrixAnswers = {};
          this.validationError = false;

          this.initializeAnswers();

          // Close popup if all questions already submitted
          if (this.surveyData.length === 0) {
            this.visible = false;
            this.$emit("update:modelValue", false);
          }
        }
      } catch (error) {
        console.error("Error fetching survey data:", error);
      } finally {
        this.isLoading = false;
      }
    },

    initializeAnswers() {
      const initial = {};

      this.activeQuestions.forEach((q, idx) => {
        switch (q.question_type.title) {
          case "checkbox":
            initial[idx] = [];
            break;

          case "consent":
            initial[idx] = false;
            break;

          case "rating":
          case "number":
          case "slider":
            initial[idx] = q.config?.min ?? null;
            break;

          case "star":
            initial[idx] = 0;
            break;

          case "date_time":
            initial[idx + "_date"] = "";
            initial[idx + "_time"] = "";
            break;

          case "readonly":
            // Set a default value for readonly questions
            initial[idx] = "read";
            break;

          case "image_choice":
            initial[idx] = null; // No image selected initially
            break;

          default:
            initial[idx] = "";
        }
      });

      this.answers = initial;
      this.matrixAnswers = {};
    },

    isQuestionAnswered(index) {
      const q = this.activeQuestions[index];
      if (!q) return false;

      const answer = this.answers[index];

      // Check based on question type
      switch (q.question_type.title) {
        case "checkbox":
          return Array.isArray(answer) && answer.length > 0;
        
        case "consent":
          return answer === true;
        
        case "date_time":
          const date = this.answers[index + "_date"];
          const time = this.answers[index + "_time"];
          return !!(date && time);
        
        case "matrix":
          const matrixAnswer = this.matrixAnswers[index];
          if (!matrixAnswer) return false;
          // Check if all rows have a selection
          return q.config.rows.every(row => matrixAnswer[row]);
        
        case "rating":
        case "number":
        case "slider":
          return answer !== null && answer !== undefined && answer !== '';
        
        case "star":
          return answer > 0;
        
        case "readonly":
          return true; // Read-only questions are always "answered"
        
        case "image_choice":
          return answer !== null && answer !== undefined && answer !== '';
        
        default:
          // For text inputs, radio, dropdown, etc.
          if (typeof answer === 'string') {
            return answer.trim().length > 0;
          }
          return answer !== null && answer !== undefined && answer !== '';
      }
    },

    validateCurrentQuestion() {
      if (this.currentQ.required) {
        const answered = this.isQuestionAnswered(this.currentIndex);
        if (!answered) {
          this.validationError = true;
          return false;
        }
      }
      this.validationError = false;
      return true;
    },

    clearValidationError() {
      this.validationError = false;
    },

    next() {
      // Validate before proceeding
      if (this.currentQ.required && !this.isQuestionAnswered(this.currentIndex)) {
        this.validationError = true;
        return;
      }

      this.validationError = false;
      if (this.currentIndex < this.activeQuestions.length - 1) {
        this.currentIndex++;
      }
    },

    prev() {
      this.validationError = false;
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    },

    selectStar(value) {
      this.answers[this.currentIndex] = value;
      this.clearValidationError();
    },

    selectYesNo(value) {
      this.answers[this.currentIndex] = value;
      this.clearValidationError();
    },

    selectTrueFalse(value) {
      this.answers[this.currentIndex] = value;
      this.clearValidationError();
    },

    selectImageChoice(index) {
      // Store the selected image index
      this.answers[this.currentIndex] = index;
      this.clearValidationError();
    },

    handleImageError(event, index) {
      // Handle image loading errors
      event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
      event.target.alt = 'Image failed to load';
    },

    submit() {
      // Validate all required questions before submitting
      const unansweredRequired = this.activeQuestions.some((q, idx) => {
        if (q.required) {
          return !this.isQuestionAnswered(idx);
        }
        return false;
      });

      if (unansweredRequired) {
        this.validationError = true;
        // Show error message
        this.messages = {
          text: "Please answer all required questions before submitting.",
          type: "error",
        };
        // Scroll to first unanswered required question
        const firstUnanswered = this.activeQuestions.findIndex((q, idx) => {
          return q.required && !this.isQuestionAnswered(idx);
        });
        if (firstUnanswered !== -1) {
          this.currentIndex = firstUnanswered;
        }
        return;
      }

      this.disable_submit = true;

      const payload = {
        survey: this.surveyId,
        answers: [],
      };

      this.activeQuestions.forEach((q, idx) => {
        let answer = this.answers[idx];

        // Matrix
        if (q.question_type?.title === "matrix") {
          answer = this.matrixAnswers[idx] || {};
        }

        // Date & Time
        if (q.question_type?.title === "date_time") {
          const date = this.answers[idx + "_date"] || "";
          const time = this.answers[idx + "_time"] || "";
          answer = date + (time ? ` ${time}` : "");
        }
        
        // Read-only - send a default value
        if (q.question_type?.title === "readonly") {
          answer = "read";
        }

        // Image Choice - send the selected image data
        if (q.question_type?.title === "image_choice") {
          if (typeof answer === 'number' && answer !== null) {
            // Get the selected option data
            const selectedOption = q.config.options[answer];
            // Send both the index and the image URL/identifier
            answer = {
              index: answer,
              image: selectedOption?.image || null,
              label: selectedOption?.label || null
            };
          } else {
            answer = null; // No selection
          }
        }
        
        // Skip if answer is empty (for non-required questions)
        if (
          answer === "" ||
          answer === null ||
          answer === undefined ||
          (Array.isArray(answer) && answer.length === 0) ||
          (typeof answer === "object" &&
            !Array.isArray(answer) &&
            Object.keys(answer).length === 0)
        ) {
          return;
        }
        
        payload.answers.push({
          question: q.id,
          answer: answer,
        });
      });

      console.log('Submitting payload:', payload); // Debug log

      axios
        .post(
          `${process.env.VUE_APP_API}/survey/submit/?Key=${this.userInfo.token_key}`,
          payload,
        )
        .then((response) => {
          if (response.data.status === 200 || response.status === 200) {
            this.messages = {
              text: response.data.message || "Survey submitted successfully.",
              type: "success",
            };

            this.submitted = true;
            setTimeout(() => {
              this.messages = {
                text: "",
                type: "null",
              };
              this.$emit("update:modelValue", false);
              this.$emit("survey-submitted");
            }, 1000);
          } else {
            this.messages = {
              text: response.data.message || "Submission failed.",
              type: "error",
            };
          }
        })
        .catch((error) => {
          console.error('Submission error:', error);
          this.messages = {
            text:
              error.response?.data?.message ||
              error.response?.data?.error ||
              "Something went wrong.",
            type: "error",
          };
        })
        .finally(() => {
          this.disable_submit = false;
        });
    },

    closePopup() {
      this.visible = false;
      this.submitted = false;
      this.currentIndex = 0;
      this.surveyData = null;
      this.validationError = false;
    },

    setMatrix(qIdx, row, col) {
      if (!this.matrixAnswers[qIdx]) this.matrixAnswers[qIdx] = {};
      this.matrixAnswers[qIdx][row] = col;
      this.matrixAnswers = { ...this.matrixAnswers };
      this.clearValidationError();
    },

    getMatrix(qIdx, row) {
      return this.matrixAnswers[qIdx]?.[row];
    },
  },
};
</script>

<style scoped>
/* Add any custom styles here */
</style>